export type FocusFirstErrorOptions = {
    container?: ParentNode | null;
    focusDelay?: number;
};

const FOCUSABLE_SELECTOR =
    'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

/** Legacy OTP field name variants only — prefer data-field for everything else. */
const FIELD_ALIASES: Record<string, string[]> = {
    code: ['otp'],
    otp: ['code', 'totp'],
    totp: ['otp'],
    one_time_password: ['code', 'otp'],
};

function escapeSelector(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }

    return value.replace(/["\\]/g, '\\$&');
}

function getErrorKeys(errors: Record<string, string | undefined>): string[] {
    return Object.keys(errors).filter((key) => Boolean(errors[key]));
}

function toCamelCase(value: string): string {
    return value.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());
}

function getSelectorCandidates(errorKey: string): string[] {
    const escaped = escapeSelector(errorKey);
    const kebab = escapeSelector(errorKey.replace(/_/g, '-'));
    const camel = escapeSelector(toCamelCase(errorKey));

    return [
        `[data-field="${escaped}"]`,
        `[name="${escaped}"]`,
        `#${escaped}`,
        `[data-field="${camel}"]`,
        `[name="${camel}"]`,
        `#${kebab}`,
        `#${camel}`,
    ];
}

function getNestedSelectorCandidates(errorKey: string): string[] {
    const candidates = getSelectorCandidates(errorKey);
    const segments = errorKey.split('.');

    if (segments.length > 1) {
        const baseKey = segments[0];
        candidates.push(...getSelectorCandidates(baseKey));
    }

    for (const alias of FIELD_ALIASES[errorKey] ?? []) {
        candidates.push(`[data-field="${escapeSelector(alias)}"]`, `#${escapeSelector(alias)}`);
    }

    return [...new Set(candidates)];
}

function isVisible(element: HTMLElement): boolean {
    if (element.offsetParent === null && getComputedStyle(element).position !== 'fixed') {
        return false;
    }

    const style = getComputedStyle(element);

    return style.visibility !== 'hidden' && style.display !== 'none';
}

function getFocusableElement(element: HTMLElement): HTMLElement | null {
    if (element.matches(FOCUSABLE_SELECTOR) && isVisible(element)) {
        return element;
    }

    const focusableChild = element.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

    if (focusableChild && isVisible(focusableChild)) {
        return focusableChild;
    }

    if (isVisible(element) && typeof element.focus === 'function') {
        return element;
    }

    return null;
}

function compareDomOrder(a: HTMLElement, b: HTMLElement): number {
    const position = a.compareDocumentPosition(b);

    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
        return -1;
    }

    if (position & Node.DOCUMENT_POSITION_PRECEDING) {
        return 1;
    }

    return 0;
}

function findFocusTargets(errorKeys: string[], root: ParentNode): HTMLElement[] {
    const targets: HTMLElement[] = [];
    const seen = new Set<HTMLElement>();

    for (const errorKey of errorKeys) {
        for (const selector of getNestedSelectorCandidates(errorKey)) {
            const elements = root.querySelectorAll<HTMLElement>(selector);

            for (const element of elements) {
                const focusable = getFocusableElement(element);

                if (focusable && !seen.has(focusable)) {
                    seen.add(focusable);
                    targets.push(focusable);
                }
            }
        }
    }

    return targets.sort(compareDomOrder);
}

export function focusFirstError(
    errors: Record<string, string | undefined>,
    options: FocusFirstErrorOptions = {},
): void {
    const errorKeys = getErrorKeys(errors);

    if (errorKeys.length === 0) {
        return;
    }

    const root = options.container ?? document;
    const focusDelay = options.focusDelay ?? 0;

    const run = (): void => {
        const targets = findFocusTargets(errorKeys, root);
        const target = targets[0];

        if (!target) {
            return;
        }

        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };

    if (focusDelay > 0) {
        window.setTimeout(run, focusDelay);

        return;
    }

    requestAnimationFrame(() => {
        requestAnimationFrame(run);
    });
}
